import React, { FC } from 'react';
import styles from './index.module.scss';
import { SwitchStatusType, SwitchStatusTypeFormat } from '@/api/circuit';
import { Tag, Tooltip } from '@maxtropy/components';
import { StationType, SwitchStatusColor } from '../../const';
import { Progress } from 'antd';
import { findItemById, groupByChildren } from '../../utils';
import classNames from 'classnames';
import { V2PowerRoomConfigPowerSystemConfigurationTreePostResponse } from '@maxtropy/device-customer-apis-v2';
import { isNil } from 'lodash-es';
import { keepPercentOrLine, keepPercentTwoNull } from '@/utils/utils';
import icon_circuit from '../imgs/icon-circuit.png';
import { calcPadding, calcProcessColor, hasChildEachLevel, noChildWrapperStyle, style } from './utils';

export interface IEachStation {
  item: any;
  data?: V2PowerRoomConfigPowerSystemConfigurationTreePostResponse;
  getItemInfo?: (visible: boolean, recordData: any) => void;
  getSelectIndex?: (val?: string) => void;
  activeIndex?: string;
}

const EachStation: FC<IEachStation> = props => {
  const { item, data, getItemInfo, getSelectIndex, activeIndex } = props;

  const handleOnClickNode = (item: any) => {
    if (item?.type === StationType.DistributionStation) {
      const res = findItemById(data, item.id);
      if (activeIndex === item.id) {
        getItemInfo?.(false, undefined);
        getSelectIndex?.(undefined);
      } else {
        getItemInfo?.(true, res);
        getSelectIndex?.(item.id);
      }
    }
  };

  // 渲染有子的节点
  const renderHasChildren = (
    item: any,
    index: number,
    level: number,
    isShowBorder: boolean,
    width: number,
    withChildren: any[]
  ) => (
    <div
      key={item.id + '-' + index}
      className={classNames(styles.level_next_wrapper, level === 2 && styles.overflow)}
      style={style(isShowBorder)}
    >
      <div className={styles.level_next_wrapper_name} style={{ width }}>
        <div
          className={classNames(styles.each_level, activeIndex === item.id ? styles.active : undefined)}
          style={hasChildEachLevel(level, item)}
          onClick={() => handleOnClickNode(item)}
        >
          <div className={styles.name} style={{ paddingBottom: calcPadding(item, false) }}>
            <Tooltip title={item.name}>{item.name}</Tooltip>
          </div>
          {(item?.monitorItemList ?? []).length > 0 && (
            <div
              className={styles.loadRate_wrapper}
              style={{
                padding: calcPadding(item, true),
              }}
            >
              {(item?.monitorItemList ?? []).map(
                (c: any, idx: number) =>
                  !isNil(c.loadRate) && (
                    <div
                      className={styles.loadRate}
                      key={c.circuitId + '-' + idx}
                      style={{ flex: withChildren.length > 1 ? 1 : '0 0 calc(50% - 4px)' }}
                    >
                      <div className={styles.label_wrapper}>
                        <div className={styles.label}>
                          <Tooltip placement="top" title={`监测回路：${c.circuitName}`}>
                            <img src={icon_circuit} className={styles.icon} alt="" />
                          </Tooltip>
                          负载率：
                        </div>
                        <div className={styles.value}>{keepPercentOrLine(c.loadRate)}%</div>
                      </div>
                      <Progress
                        className={styles.process}
                        percent={!isNil(c.loadRate) ? keepPercentTwoNull(c.loadRate)! : undefined}
                        showInfo={false}
                        strokeColor={calcProcessColor(keepPercentTwoNull(c.loadRate))}
                        size="small"
                        trailColor="rgba(0,0,0,0.5)"
                      />
                    </div>
                  )
              )}
            </div>
          )}
        </div>
      </div>

      {item.children && item.children.length > 0 && (
        <div className={styles.level_next}> {renderChildren(item.children, 500, false, level + 1)}</div>
      )}
    </div>
  );

  // 渲染无子的节点
  const renderNoChild = (isShowBorder: boolean, width: number, withoutChildren: any[], level: number) => (
    <div className={styles.level_next_noChildren} style={style(isShowBorder)}>
      <div className={styles.level_next_noChildren_wrapper} style={{ width, flexShrink: 0 }}>
        {(withoutChildren ?? []).map((item: any, idx: number) => (
          <div
            key={item.id + '-' + idx}
            className={classNames(styles.noChildren_each_level, activeIndex === item.id ? styles.active : undefined)}
            style={noChildWrapperStyle(level, width, withoutChildren, item)}
            onClick={() => handleOnClickNode(item)}
          >
            <div
              className={styles.name}
              style={{
                paddingBottom: calcPadding(item, false),
              }}
            >
              <Tooltip title={item.name}>{item.name}</Tooltip>
            </div>
            {(item?.monitorItemList ?? []).length > 0 && (
              <div
                className={styles.loadRate_wrapper}
                style={{
                  padding: calcPadding(item, true),
                }}
              >
                {(item?.monitorItemList ?? []).map(
                  (c: any, idx2: number) =>
                    !isNil(c.loadRate) && (
                      <div
                        className={styles.loadRate}
                        key={c.id + '-' + idx2}
                        style={{ flex: withoutChildren.length > 1 ? 1 : '0 0 calc(50% - 4px)' }}
                      >
                        <div className={styles.label_wrapper}>
                          <div className={styles.label}>
                            <Tooltip placement="top" title={`监测回路：${c.circuitName}`}>
                              <img src={icon_circuit} className={styles.icon} alt="" />
                            </Tooltip>
                            负载率：
                          </div>
                          <div className={styles.value}>{keepPercentOrLine(c.loadRate)}%</div>
                        </div>

                        <Progress
                          className={styles.process}
                          percent={!isNil(c.loadRate) ? keepPercentTwoNull(c.loadRate)! : undefined}
                          showInfo={false}
                          strokeColor={calcProcessColor(keepPercentTwoNull(c.loadRate))}
                          trailColor="rgba(0,0,0,0.5)"
                        />
                      </div>
                    )
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // 渲染子节点
  const renderChildren = (children: any[], width: number, isShowBorder: boolean, level = 2) => {
    const { withChildren, withoutChildren } = groupByChildren(children);
    return (
      <>
        {(withChildren ?? []).map((item: any, idx: number) =>
          renderHasChildren(item, idx, level, isShowBorder, width, withChildren)
        )}

        {withoutChildren && withoutChildren.length > 0 && renderNoChild(isShowBorder, width, withoutChildren, level)}
      </>
    );
  };

  return (
    <div className={styles.each_content}>
      {/* 第一级 - 配电柜 */}
      <div className={styles.level1}>
        <div className={styles.level1_name}>{item.circuitName}</div>
        <div className={styles.level1_switch}>
          <Tag border="solid" color={SwitchStatusColor[item.circuitSwitchStatus as SwitchStatusType]}>
            {SwitchStatusTypeFormat[item.circuitSwitchStatus as SwitchStatusType]}
          </Tag>
        </div>
        <div className={styles.dashLine}></div>
        <div className={styles.fuzai}>
          <div className={styles.name}>负载率</div>
          <div className={styles.progress}>
            {!isNil(item?.loadRate) ? (
              <Tooltip title={`${keepPercentTwoNull(item.loadRate)}%`}>
                <Progress
                  type="circle"
                  size={60}
                  percent={!isNil(item.loadRate) ? keepPercentTwoNull(item.loadRate)! : undefined}
                  strokeColor={calcProcessColor(keepPercentTwoNull(item.loadRate))}
                  trailColor="rgba(0,0,0,0.5)"
                  format={() => {
                    if (keepPercentTwoNull(item.loadRate)! >= 100) return `${keepPercentTwoNull(item.loadRate)}%`;
                    return `${keepPercentTwoNull(item.loadRate)}%`;
                  }}
                />
              </Tooltip>
            ) : (
              <span style={{ color: '#999' }}>--</span>
            )}
          </div>
        </div>
        <div className={styles.top_line}></div>
        <div className={styles.bottom_line}></div>
      </div>

      {/* 母排 */}
      <div className={styles.level_next_2}>
        {item.powerSystemBusPaiList.map((c: any, index: number) => (
          <div key={c.id + '-' + index} className={styles.level_next_2_wrapper}>
            <div className={styles.level_next_2_wrapper_name}>
              <div className={styles.each_level2}>{c.busPaiName}</div>
            </div>
            <div className={styles.level_next}>
              {c?.powerSystemConfigurationTree &&
                c?.powerSystemConfigurationTree.length > 0 &&
                renderChildren(c?.powerSystemConfigurationTree, 328, true)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EachStation;
